import { useEffect, useState } from "react";
import { ExternalLink, Scale, Sparkles } from "lucide-react";
import { api, ApiError } from "../api/client";
import { card, theme, type as text } from "../theme";
import { libraryPath, playbookPath } from "../lib/playbooks";
import { ShellLink } from "./ShellLink";

// A decision the published guidance leaves to a person, shown where the person makes it.
// Two things, labelled as two things: what the guidance says, verbatim and cited, and the
// AI's recommendation for this organisation — weighed against its laws, obligations,
// insurance and records (POST /help/playbooks/:id/decisions/:decisionId/recommendation).
// Help shows only the first. Nothing here records a decision.

interface Quote { doc: string; ref?: string; text: string }
interface SourceDoc { id: string; title: string; publisher: string; readable: boolean; sourceUrl: string }
interface Decision { id: string; question: string; positions: Quote[] }
interface Recommendation {
  recommendation: string; reasoning: string; factors: string[]; unknowns: string[]; consult: string[]; guidanceCited: number[];
}

const linkStyle = { color: theme.color.accentText, textDecoration: "none", fontWeight: 600 } as const;
const MAX_NOTE = 1500;

export function PlaybookDecision({ playbook, decision, context }: {
  playbook: string;
  decision: string;
  /** What the page already knows about the situation (e.g. the incident's summary), offered as a starting note. */
  context?: string;
}) {
  const [data, setData] = useState<{ playbook: { id: string; title: string }; decision: Decision; sources: SourceDoc[] } | null>(null);
  const [note, setNote] = useState(context?.slice(0, MAX_NOTE) ?? "");
  const [rec, setRec] = useState<Recommendation | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.get<NonNullable<typeof data>>(`/help/playbooks/${encodeURIComponent(playbook)}/decisions/${encodeURIComponent(decision)}`)
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData(null); });
    return () => { cancelled = true; };
  }, [playbook, decision]);

  if (!data) return null;
  const docs = new Map(data.sources.map((s) => [s.id, s]));

  async function recommend() {
    setBusy(true); setError(null);
    try {
      const r = await api.post<{ recommendation: Recommendation }>(
        `/help/playbooks/${encodeURIComponent(playbook)}/decisions/${encodeURIComponent(decision)}/recommendation`,
        { note: note.trim() || undefined },
      );
      setRec(r.recommendation);
    } catch (e) {
      if (e instanceof ApiError && e.status === 402 && e.code === "tier_locked") {
        setError("AI recommendations come with the Self-Service + AI service level and above.");
      } else {
        setError(e instanceof ApiError ? e.message : "Could not get a recommendation. Please try again.");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <section style={{ ...card, padding: "12px 16px", margin: "12px 0" }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
        <Scale size={16} style={{ color: theme.color.accentText }} />
        <span style={{ ...text.sectionTitle }}>{data.decision.question}</span>
      </div>
      <p style={{ ...text.hint, margin: "0 0 8px" }}>
        A decision for your organisation. Below is what the published guidance says, word for word, and — if you ask for it — the AI's recommendation for you.{" "}
        <ShellLink to={playbookPath(data.playbook.id)} style={linkStyle}>{data.playbook.title} playbook</ShellLink>
      </p>

      <div style={{ ...text.hint, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", marginTop: 6 }}>What the guidance says</div>
      {data.decision.positions.map((q, i) => {
        const doc = docs.get(q.doc);
        return (
          <figure key={i} style={{ margin: "8px 0 0", padding: "8px 12px", borderLeft: `3px solid ${theme.color.accent}`, background: "var(--c-panelAlt)", borderRadius: 3 }}>
            <blockquote style={{ margin: 0, fontSize: 13, lineHeight: 1.6 }}><span style={{ ...text.hint, marginRight: 6 }}>[{i + 1}]</span>“{q.text}”</blockquote>
            <figcaption style={{ ...text.hint, marginTop: 4 }}>
              {doc?.readable
                ? <ShellLink to={libraryPath(doc.id)} style={linkStyle}>{doc.publisher} — {doc.title}</ShellLink>
                : <a href={doc?.sourceUrl} target="_blank" rel="noopener noreferrer" style={linkStyle}>{doc ? `${doc.publisher} — ${doc.title}` : q.doc} <ExternalLink size={11} /></a>}
              {q.ref ? `, ${q.ref}` : ""}
            </figcaption>
          </figure>
        );
      })}

      <div style={{ ...text.hint, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", marginTop: 14 }}>The AI's recommendation for you</div>
      {!rec && (
        <div style={{ marginTop: 6 }}>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, MAX_NOTE))}
            rows={3}
            placeholder="Optional: anything about this situation the AI should know (it already sees your laws, obligations, insurance and registers)"
            style={{ width: "100%", padding: 8, borderRadius: 4, border: `1px solid ${theme.color.border}`, background: theme.color.panel, color: theme.color.text, fontSize: 12.5, resize: "vertical" }}
          />
          <button
            onClick={recommend}
            disabled={busy}
            style={{ marginTop: 6, display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 4, border: `1px solid ${theme.color.accent}`, background: "transparent", color: theme.color.accentText, fontWeight: 600, cursor: busy ? "wait" : "pointer" }}
          >
            <Sparkles size={14} /> {busy ? "Weighing your situation…" : "Get a recommendation"}
          </button>
          {error && <p style={{ ...text.hint, color: theme.color.dangerText, margin: "6px 0 0" }}>{error}</p>}
        </div>
      )}
      {rec && (
        <div style={{ marginTop: 8, padding: "10px 12px", border: `1px dashed ${theme.color.border}`, borderRadius: 4 }}>
          <p style={{ margin: "0 0 6px", fontWeight: 600, color: theme.color.heading }}>{rec.recommendation}</p>
          {rec.reasoning && <p style={{ ...text.hint, margin: "0 0 6px" }}>{rec.reasoning}{rec.guidanceCited.length > 0 ? ` (guidance ${rec.guidanceCited.map((n) => `[${n}]`).join(" ")})` : ""}</p>}
          {rec.factors.length > 0 && <List title="Laws and factors weighed" items={rec.factors} />}
          {rec.unknowns.length > 0 && <List title="What would change this" items={rec.unknowns} />}
          {rec.consult.length > 0 && <List title="Who to ask" items={rec.consult} />}
          <p style={{ ...text.hint, margin: "8px 0 0", color: theme.color.faint }}>
            Written by AI from your records. It is a recommendation, not legal advice; the decision is yours.
          </p>
          <button onClick={() => setRec(null)} style={{ marginTop: 6, background: "none", border: "none", padding: 0, ...linkStyle, cursor: "pointer", fontSize: 12 }}>Ask again</button>
        </div>
      )}
    </section>
  );
}

function List({ title, items }: { title: string; items: string[] }) {
  return (
    <div style={{ marginTop: 6 }}>
      <span style={{ ...text.hint, fontWeight: 600 }}>{title}</span>
      <ul style={{ margin: "2px 0 0", paddingLeft: 18 }}>
        {items.map((x, i) => <li key={i} style={{ ...text.hint }}>{x}</li>)}
      </ul>
    </div>
  );
}
